/**
 * Public-website games API client (task 6.1, public-website spec).
 *
 * Wraps the public `GameSearchApi` v1 operations
 * `SearchGamesV1` and `GetGameByIdV1` per design §3.1 / §3.4 with
 * the in-browser SWR cache from task 4.6 (`./swr-cache.ts`). Both
 * operations are public REST routes; the SWR layer keys responses
 * on URL + auth headers per design §5.2 and Requirement 10.2.
 *
 * The 200 ms debounce on the search input enforced by Requirement
 * 20.6 lives in the component layer (`<GamesPage>` task 6.5/6.8) —
 * **never** in this client. The client is a thin wrapper so the
 * component-layer debounce composes cleanly with SWR cache reads
 * (a debounced `getGamesSearch` call still benefits from the cache
 * on identical `(q, page, orderBy)` triples).
 *
 * TODO(smithy-follow-up-A): Per the tasks.md "Implementation gating"
 * preamble, the same Smithy follow-up that adds the new
 * `ApprovedContentApi` operations (consumed in
 * `./content-api-client.ts`) will also extend `GameSearchApi` v1's
 * IDL with the `orderBy` query input (`release-date-desc` per
 * R20.7) and the richer `GameSummary` / `GameDetail` shapes
 * referenced by the design (release-date sort, gallery,
 * articles-mentioned). Until those regenerated TS types arrive in
 * `@dvgup/contracts/generated/game-search-api`, the request /
 * response shapes for the operations consumed here are declared
 * inline below, mirroring the design §3.1 + Requirement 3 / 20
 * surface verbatim. Migration once follow-up A lands: replace
 * each inline interface with a
 * `import type { ... } from '@dvgup/contracts/generated/game-
 * search-api'` entry. The two function signatures, the exported
 * names, and every consumer callsite stay identical — the
 * migration is a single-import swap, identical to the
 * `content-api-client.ts` migration plan.
 *
 * The SPA never constructs the API origin directly: every request
 * goes through the CloudFront `/api/*` cache behavior (task 3.4) so
 * the 15-minute api-passthrough cache and the per-`/Profile*V1`
 * bypass behaviors apply uniformly. Callers pass `baseUrl: "/api"`
 * (production) or the dev-server proxy origin (local).
 *
 * Function-length budget: every public export below stays under 20
 * lines per Requirement 3.7.
 */

import { swrFetch, type SwrSubscription } from './swr-cache';

/**
 * Compact game search-result row per Requirement 3.4 + 20.2 +
 * design §6.5 (Games landing). Mirrors the shape the SPA actually
 * needs to render `<GameCard>`.
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/game-search-api`.
 */
export interface GameSummary {
  /** Stable identifier used as the `:gameId` route param on `/games/:gameId`. */
  readonly id: string;
  readonly title: string;
  /** ISO-8601 calendar date (YYYY-MM-DD) per Requirement 20.2. */
  readonly releaseDate: string;
  /** Platform identifiers resolved against `BrandingConfig.platformIcons` per R20.9. */
  readonly platforms: ReadonlyArray<string>;
  /** Absolute URL to the cover image; absent when the record has no cover yet. */
  readonly coverImageUrl?: string;
}

/**
 * Affiliate-link reference inlined in Game-page payloads per
 * Requirement 3.2 / R8.6. The sub-spec hydrates these via the
 * signed-URL transport when empty (task 6.4); when present here
 * they are rendered directly.
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/game-search-api`.
 */
export interface GameAffiliateLink {
  /** Vendor identifier (e.g., `amazon`) — drives the `<AffiliateLinks>` icon. */
  readonly vendor: string;
  readonly url: string;
}

/**
 * Cross-link entry referenced from a Game-page's "articles
 * mentioned" list per Requirement 3.2.9. Each entry deep-links to
 * `/daily/{date}#content-{contentId}`.
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/game-search-api`.
 */
export interface ArticleMentioned {
  readonly contentId: string;
  /** ISO-8601 calendar date (YYYY-MM-DD) — drives the `/daily/{date}` link target. */
  readonly date: string;
  readonly title: string;
}

/**
 * Full Game-page record per Requirement 3.2 + 20.2. Extends
 * {@link GameSummary} with the rendering surface unique to the
 * detail page (summary text, gallery tiles, affiliate links,
 * article cross-links).
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/game-search-api`.
 */
export interface GameDetail extends GameSummary {
  readonly canonicalTitle: string;
  readonly summary: string;
  /** Studios credited with developing the game (round-26, first-class facet). */
  readonly developers: ReadonlyArray<string>;
  /** Publishing houses — distinct from developers. */
  readonly publishers: ReadonlyArray<string>;
  /** 1-4 primary genre tags, e.g. ["Metroidvania", "Action-adventure"]. */
  readonly genres: ReadonlyArray<string>;
  /** Per-platform launch dates (round-26). Simultaneous launches ship N entries with the same date. */
  readonly releases: ReadonlyArray<GameRelease>;
  /** Consolidated media envelope (hero + gallery + reserved clips). */
  readonly media: GameMedia;
  /** Legacy flat affiliate-links list — kept for the pre-round-26 affiliate-hydration path. */
  readonly affiliateLinks: ReadonlyArray<GameAffiliateLink>;
  /**
   * Unified purchase links (round-26). Each row carries a vendor
   * + label + url + `isAffiliate` + optional per-vendor metadata.
   * Includes Steam, platform stores, dev-site Buy affordances, and
   * (future) tagged Amazon product deep-links via affiliate-integration.
   */
  readonly purchaseLinks: ReadonlyArray<GamePurchaseLink>;
  /** Legacy `articlesMentioned` triples list — kept for backward compat with the pre-rewrite thunk. */
  readonly articlesMentioned: ReadonlyArray<ArticleMentioned>;
  /**
   * Raw `articles_mentioned` uuid list — the canonical pointer set
   * the "Recent coverage" panel reads. Fans out through
   * `POST /GetApprovedContentByIdV1`.
   */
  readonly articlesMentionedUuids: ReadonlyArray<string>;
  /** Audit trail of third-party sources the content agent consulted. */
  readonly contentSources: ReadonlyArray<GameContentSource>;
}

/** Per-platform release-date row (round-26). */
export interface GameRelease {
  readonly platform: string;
  readonly date: string;
}

/** Consolidated media envelope on the Game page. */
export interface GameMedia {
  readonly hero: GameMediaItem;
  readonly gallery: ReadonlyArray<GameMediaItem>;
  readonly clips: ReadonlyArray<GameMediaClip>;
}

/** Hero / gallery image entry inside `GameMedia`. */
export interface GameMediaItem {
  readonly kind: 'image';
  readonly url: string;
  readonly source: string;
  readonly width?: number;
  readonly height?: number;
}

/** Reserved audio / video clip entry under `GameMedia.clips` (round-27). */
export interface GameMediaClip {
  readonly kind: 'podcast-audio' | 'trailer-video' | 'interview-audio' | 'interview-video';
  readonly source: string;
  readonly url: string;
  readonly title: string;
  readonly durationSeconds?: number;
  readonly publishedAt?: string;
}

/** Unified purchase-link row on the Game page (round-26). */
export interface GamePurchaseLink {
  readonly vendor:
    | 'amazon' | 'steam' | 'playstation-store' | 'microsoft-store'
    | 'nintendo-eshop' | 'gog' | 'epic-games-store' | 'dev-site';
  readonly label: string;
  readonly url: string;
  readonly isAffiliate: boolean;
  /** Free-form per-vendor identifiers (e.g. `{app_id: "..."}` or `{asin: "..."}`). */
  readonly vendorMetadata?: Record<string, unknown>;
}

/** Source audit entry — kind + url + how the content agent used it. */
export interface GameContentSource {
  readonly kind: string;
  readonly url: string;
  readonly note: string;
}

/**
 * Page slice returned by {@link getGamesSearch}. Pagination is
 * server-pinned to 20 items per page per Property 17 / R20.8; the
 * client renders `total` + `pageCount` to drive MUI `Pagination`.
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/game-search-api`.
 */
export interface GamesSearchPage {
  readonly items: ReadonlyArray<GameSummary>;
  readonly page: number;
  readonly pageCount: number;
  readonly total: number;
}

/**
 * Sort order accepted by {@link getGamesSearch}. The Games landing
 * (empty `q`) defaults to `release-date-desc` per Requirement 20.7;
 * a future follow-up may add `relevance` for active queries.
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/game-search-api`.
 */
export type GamesSearchOrderBy = 'release-date-desc';

/**
 * Input shape for {@link getGamesSearch} per Requirement 20.1 +
 * 20.7. `q` may be empty (default-landing experience); `page`
 * defaults to 1 server-side; `orderBy` defaults to
 * `release-date-desc` server-side.
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/game-search-api`.
 */
export interface GetGamesSearchInput {
  readonly q: string;
  /** 1-indexed page; server defaults to 1 when omitted. */
  readonly page?: number;
  /** Sort order; server defaults to `release-date-desc` when omitted. */
  readonly orderBy?: GamesSearchOrderBy;
}

/**
 * Input shape for {@link getGameById}. The `id` matches the
 * `:gameId` URL segment on `/games/:gameId`.
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/game-search-api`.
 */
export interface GetGameByIdInput {
  readonly id: string;
}

/**
 * Output shape for {@link getGameById}. Mirrors the design's
 * `POST /GetGameByIdV1` envelope (single-record `item`).
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/game-search-api`.
 */
export interface GetGameByIdOutput {
  readonly item: GameDetail;
}

/**
 * Per-call options for the games API client. The caller threads
 * the CloudFront-fronted base URL and the public REST API key
 * through (read from `/config.json` at SPA bootstrap per design
 * §3.1 / `./config.ts`).
 */
export interface GamesApiClientOptions {
  /** API origin — `"/api"` in production, dev-server proxy origin locally. */
  readonly baseUrl: string;
  /** Public REST API key from `/config.json`; injected as `x-api-key`. */
  readonly apiKey?: string;
}

/**
 * Fetch a page of games matching the search input per Requirement
 * 3.4 / 20.1 / 20.7. Empty `input.q` triggers the default-landing
 * experience (most-recent-by-release-date list per R20.7); a
 * non-empty `q` issues a real search. A 200 with `items: []` is
 * normal per R12.6 (no error — the caller renders `<EmptyResults>`
 * per R8.11).
 *
 * @param input The search input (`q` required, `page` / `orderBy` optional).
 * @param options Base URL + API key sourced from `/config.json`.
 * @param subscription Optional callback fired when background SWR
 *   revalidation finds a different body than the cached body.
 * @returns The matching page slice + pagination metadata.
 */
export async function getGamesSearch(
  input: GetGamesSearchInput,
  options: GamesApiClientOptions,
  subscription?: SwrSubscription<GamesSearchPage>,
): Promise<GamesSearchPage> {
  return swrFetch<GamesSearchPage>(
    `${options.baseUrl}/SearchGamesV1`,
    {
      apiKey: options.apiKey,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
    subscription,
  );
}

/**
 * Fetch a single Game-page record per Requirement 3.2 + 20.2.
 * Used by `<GamePage>` (`/games/:gameId`) on mount.
 *
 * @param input The game id to fetch.
 * @param options Base URL + API key sourced from `/config.json`.
 * @param subscription Optional callback fired when background SWR
 *   revalidation finds a different body than the cached body.
 * @returns The full Game-page record envelope.
 */
export async function getGameById(
  input: GetGameByIdInput,
  options: GamesApiClientOptions,
  subscription?: SwrSubscription<GetGameByIdOutput>,
): Promise<GetGameByIdOutput> {
  const wire = await swrFetch<WireGetGameByIdOutput>(
    `${options.baseUrl}/GetGameByIdV1`,
    {
      apiKey: options.apiKey,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ game_id: input.id }),
    },
    subscription as SwrSubscription<WireGetGameByIdOutput> | undefined,
  );
  return projectWireGameDetail(wire);
}

/**
 * Wire body returned by `POST /GetGameByIdV1`. Field names are
 * snake_case per the Smithy IDL (`models/smithy/game-search-
 * api.smithy`). The SPA-facing {@link GetGameByIdOutput} is
 * camelCase; {@link projectWireGameDetail} projects between the
 * two.
 *
 * TODO(smithy-follow-up-A): once follow-up A regenerates the
 * TS types with camelCase members, this interface + the
 * projection helper collapse — the `swrFetch` call typed on
 * `GetGameByIdOutput` returns the projected shape directly.
 */
interface WireGetGameByIdOutput {
  readonly item: {
    readonly game_id: string;
    readonly title: string;
    readonly primary_image: string;
    readonly platforms: ReadonlyArray<string>;
    readonly summary: string;
    readonly screenshots: ReadonlyArray<string>;
    readonly videos: ReadonlyArray<string>;
    readonly articles_mentioned: ReadonlyArray<string>;
    readonly affiliate_links: ReadonlyArray<{
      readonly asin: string;
      readonly url: string;
      readonly title: string;
      readonly image: string;
    }>;
    /**
     * Optional on the wire — the content-promoter's release-
     * enrichment step (2026-09-25) populates it; earlier Games
     * rows seeded before the enrichment shipped may not have it,
     * in which case the field is undefined / absent.
     */
    readonly release_date?: string;
    readonly source_url?: string;
  };
}

/**
 * Project the wire snake_case `GameDetail` shape onto the SPA's
 * camelCase surface consumed by `<GamePage>` and the games slice
 * (`loadGameDetail` reads `.affiliateLinks.length`).
 *
 * Field mapping (wire → SPA):
 *   - `game_id` → `id`
 *   - `title` → `title`
 *   - `primary_image` → `coverImageUrl` (dropped when empty)
 *   - `platforms` → `platforms`
 *   - `summary` → `summary`
 *   - `screenshots` → `gallery`
 *   - `affiliate_links[]` → `affiliateLinks[]` (`title` → `vendor`,
 *      `url` → `url`; other affiliate-ref fields dropped)
 *   - `articles_mentioned` (string[]) → `articlesMentioned` (empty
 *      list until follow-up A extends the wire to carry
 *      `contentId` / `date` / `title` triples per R3.2.9);
 *      preserved verbatim as `articlesMentionedUuids` for the
 *      round-26 R20.6 "Recent coverage" panel to fan out against
 *      `POST /GetApprovedContentByIdV1`.
 *   - `release_date`: forwarded verbatim as `releaseDate` per R20.2
 *      once the content-promoter's release-enrichment step populates
 *      the Games row (landed 2026-09-25). Empty string when the game
 *      has no promoted release yet.
 */
function projectWireGameDetail(wire: WireGetGameByIdOutput): GetGameByIdOutput {
  const w = wire.item as Record<string, unknown>;
  const releaseDate = typeof w['release_date'] === 'string' ? w['release_date'] as string : '';
  const platforms = Array.isArray(w['platforms']) ? (w['platforms'] as unknown[]).filter((p): p is string => typeof p === 'string') : [];
  const media = projectMedia(w['media']);
  const coverImageUrl = media.hero.url.length > 0 ? media.hero.url : undefined;
  return {
    item: {
      id: w['game_id'] as string,
      title: w['title'] as string,
      canonicalTitle: typeof w['canonical_title'] === 'string' ? w['canonical_title'] as string : w['title'] as string,
      releaseDate,
      platforms,
      ...(coverImageUrl !== undefined ? { coverImageUrl } : {}),
      summary: typeof w['summary'] === 'string' ? w['summary'] as string : '',
      developers: projectStringArray(w['developers']),
      publishers: projectStringArray(w['publishers']),
      genres: projectStringArray(w['genres']),
      releases: projectReleases(w['releases']),
      media,
      affiliateLinks: projectAffiliateLinks(w['affiliate_links']),
      purchaseLinks: projectPurchaseLinks(w['purchase_links']),
      articlesMentioned: [],
      articlesMentionedUuids: projectStringArray(w['articles_mentioned']),
      contentSources: projectContentSources(w['content_sources']),
    },
  };
}

/** Narrow a wire value to a string array. */
function projectStringArray(v: unknown): ReadonlyArray<string> {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string');
}

/** Project per-platform releases from the wire. */
function projectReleases(v: unknown): ReadonlyArray<GameRelease> {
  if (!Array.isArray(v)) return [];
  const out: GameRelease[] = [];
  for (const raw of v) {
    if (raw === null || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const platform = r['platform'];
    const date = r['date'];
    if (typeof platform !== 'string' || typeof date !== 'string') continue;
    out.push({ platform, date });
  }
  return out;
}

/**
 * Project the consolidated `media` envelope from the wire. Returns
 * a synthesized envelope with an empty hero when the field is
 * absent (older rows seeded before the round-26 rewrite).
 */
function projectMedia(v: unknown): GameMedia {
  const emptyHero: GameMediaItem = { kind: 'image', url: '', source: 'none' };
  if (v === null || typeof v !== 'object') return { hero: emptyHero, gallery: [], clips: [] };
  const m = v as Record<string, unknown>;
  const hero = projectMediaItem(m['hero']) ?? emptyHero;
  const gallery = projectMediaItemList(m['gallery']);
  const clips = projectMediaClipList(m['clips']);
  return { hero, gallery, clips };
}

/** Project one media item (hero or gallery tile). */
function projectMediaItem(v: unknown): GameMediaItem | undefined {
  if (v === null || typeof v !== 'object') return undefined;
  const m = v as Record<string, unknown>;
  const url = typeof m['url'] === 'string' ? m['url'] as string : '';
  const source = typeof m['source'] === 'string' ? m['source'] as string : 'unknown';
  const width = typeof m['width'] === 'number' ? m['width'] as number : undefined;
  const height = typeof m['height'] === 'number' ? m['height'] as number : undefined;
  return {
    kind: 'image', url, source,
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
  };
}

/** Project the gallery list. */
function projectMediaItemList(v: unknown): ReadonlyArray<GameMediaItem> {
  if (!Array.isArray(v)) return [];
  const out: GameMediaItem[] = [];
  for (const raw of v) {
    const item = projectMediaItem(raw);
    if (item !== undefined) out.push(item);
  }
  return out;
}

/** Project the reserved clips list. */
function projectMediaClipList(v: unknown): ReadonlyArray<GameMediaClip> {
  if (!Array.isArray(v)) return [];
  const out: GameMediaClip[] = [];
  for (const raw of v) {
    if (raw === null || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const kind = r['kind'];
    if (!isMediaClipKind(kind)) continue;
    out.push({
      kind, source: (r['source'] as string) ?? '',
      url: (r['url'] as string) ?? '', title: (r['title'] as string) ?? '',
      ...(typeof r['duration_seconds'] === 'number' ? { durationSeconds: r['duration_seconds'] as number } : {}),
      ...(typeof r['published_at'] === 'string' ? { publishedAt: r['published_at'] as string } : {}),
    });
  }
  return out;
}

function isMediaClipKind(v: unknown): v is GameMediaClip['kind'] {
  return v === 'podcast-audio' || v === 'trailer-video' ||
         v === 'interview-audio' || v === 'interview-video';
}

/**
 * Project the legacy flat `affiliate_links` field. The round-26 wire
 * drops this field; this projection returns `[]` on absent / empty
 * input so pre-rewrite consumers that read `affiliateLinks` keep
 * working.
 */
function projectAffiliateLinks(v: unknown): ReadonlyArray<GameAffiliateLink> {
  if (!Array.isArray(v)) return [];
  const out: GameAffiliateLink[] = [];
  for (const raw of v) {
    if (raw === null || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    if (typeof r['url'] !== 'string') continue;
    out.push({ vendor: (r['title'] as string) ?? 'amazon', url: r['url'] as string });
  }
  return out;
}

const PURCHASE_VENDORS: ReadonlyArray<GamePurchaseLink['vendor']> = [
  'amazon', 'steam', 'playstation-store', 'microsoft-store',
  'nintendo-eshop', 'gog', 'epic-games-store', 'dev-site',
];

/**
 * Snake-case wire → camel-case client for round-26 purchase_links.
 * `vendor_metadata` on the wire is a Smithy discriminated union
 * (`{steam: {app_id: "..."}}` etc.); on the client we flatten it to
 * the inner object so consumers key off `metadata.app_id` directly.
 */
function projectPurchaseLinks(raw: unknown): ReadonlyArray<GamePurchaseLink> {
  if (!Array.isArray(raw)) return [];
  const result: GamePurchaseLink[] = [];
  for (const r of raw) {
    if (r === null || typeof r !== 'object') continue;
    const parsed = parsePurchaseLink(r as Record<string, unknown>);
    if (parsed !== undefined) result.push(parsed);
  }
  return result;
}

/** Validate + reshape one purchase-link wire row. */
function parsePurchaseLink(r: Record<string, unknown>): GamePurchaseLink | undefined {
  const vendor = r['vendor'];
  const label = r['label'];
  const url = r['url'];
  if (typeof vendor !== 'string' ||
      !PURCHASE_VENDORS.includes(vendor as GamePurchaseLink['vendor']) ||
      typeof label !== 'string' || typeof url !== 'string') return undefined;
  const isAffiliate = r['is_affiliate'] === true || r['isAffiliate'] === true;
  const vendorMetadata = flattenVendorMetadata(r['vendor_metadata'] ?? r['vendorMetadata']);
  return {
    vendor: vendor as GamePurchaseLink['vendor'],
    label, url, isAffiliate,
    ...(vendorMetadata !== undefined ? { vendorMetadata } : {}),
  };
}

/**
 * Flatten the Smithy union wire shape (`{steam: {...}}`) into the
 * inner map. Accepts both the union form AND the already-flattened
 * form (for backward compat with pre-regen test fixtures).
 */
function flattenVendorMetadata(v: unknown): Record<string, unknown> | undefined {
  if (v === null || typeof v !== 'object') return undefined;
  const o = v as Record<string, unknown>;
  const keys = Object.keys(o);
  if (keys.length === 1) {
    const inner = o[keys[0]];
    if (inner !== null && typeof inner === 'object') {
      return inner as Record<string, unknown>;
    }
  }
  return o;
}

/** Snake-case wire → camel-case client for content_sources. */
function projectContentSources(
  raw: unknown,
): ReadonlyArray<GameContentSource> {
  if (!Array.isArray(raw)) return [];
  const srcList = raw as ReadonlyArray<Record<string, unknown>>;
  const result: GameContentSource[] = [];
  for (const r of srcList) {
    const kind = r['kind'];
    const url = r['url'];
    const note = r['note'];
    if (typeof kind !== 'string' || typeof url !== 'string' ||
        typeof note !== 'string') continue;
    result.push({ kind, url, note });
  }
  return result;
}
